import { useMutableRef } from "./../../../tabs-container/components/pageStore/hook";
import { useReducer, useRef } from "react";
import { cloneDeep } from "lodash";
import {
  batchUpdateDeepValue,
  deleteDeepValue,
  getDeepValue,
  updateDeepValue,
} from "./utils";
import type {
  BatchOMap,
  KeyPath,
  QueryOptions,
  SourceObject,
  UseQueryProps,
} from "./dto";

/**
 * 查询条件hook
 */
const useQuery = <T extends SourceObject = any>(
  initValue: T,
  options?: QueryOptions
): UseQueryProps<T> => {
  const { reset, mutex, allowReset, setAllowReset } = options || {};

  /** 缓存初始值 */
  const initRef = useRef(cloneDeep(initValue));
  /** 缓存重置项 */
  const resetRef = useRef(cloneDeep(reset));
  /** 缓存互斥项 */
  const mutexRef = useRef(cloneDeep(mutex));
  /** 重置锁 */
  const allowResetRef = useMutableRef(allowReset);

  /** 聚合处理函数 */
  function reducer(state: T, picker?: "RESET" | BatchOMap) {
    if (picker === "RESET") {
      return initValue;
    }
    if (picker) {
      const res: T = cloneDeep(state);
      /** 如果picker中指定了互斥项，则首先清除该组互斥项 */
      if (mutexRef.current?.length) {
        const stringMutex: Record<string, KeyPath[]> = {};
        mutexRef.current.forEach((m) => {
          return m.forEach((a) => {
            if (Array.isArray(a)) {
              stringMutex[a.join("")] = m;
            } else stringMutex[a] = m;
          });
        });
        for (const str of Object.getOwnPropertyNames(stringMutex)) {
          for (const key of picker.keys()) {
            if ((Array.isArray(key) && key.join("") === str) || str === key) {
              stringMutex[str].forEach((k) => deleteDeepValue(res, k));
            }
          }
        }
      }
      batchUpdateDeepValue(res, picker, initRef.current);
      /** 如果指定的不是重置项,则默认重置 */
      if (resetRef.current?.length && allowResetRef.current) {
        const strArr = resetRef.current.map((m) =>
          Array.isArray(m) ? m.join("") : m
        );
        const keyArr = [];
        for (const key of picker.keys()) {
          const k = Array.isArray(key) ? key.join("") : key;
          keyArr.push(k);
        }
        if (!keyArr.some((k) => strArr.includes(k))) {
          resetRef.current.forEach((k) => {
            const v = getDeepValue(initRef.current, k);
            updateDeepValue(res, k, v, initRef.current);
          });
          setAllowReset?.(false);
        }
      }

      return res;
    }

    return { ...state };
  }

  /** 接受初始值，并按模版处理 */
  const [query, dispatch] = useReducer(reducer, initValue);

  return [query, dispatch];
};

export default useQuery;
